import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faCodeBranch,
  faClipboardCheck,
  faShieldHalved,
  faEye,
  faUserCheck,
  faCircleXmark,
  faInbox,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { ROUTES } from '@/routes/routes';
import type { RequestCenterCounts } from '@/services/dashboard.service';

interface CounterRowProps {
  icon: IconDefinition;
  iconCls: string;
  label: string;
  count: number;
  /**
   * The feature genuinely does not exist yet. Keeps the honest "soon" chip
   * instead of a fabricated 0 — see the comment on the card below.
   */
  isFuture?: boolean;
  /** Destination for this specific row. Omitted → the row is not clickable. */
  to?: string;
  testId?: string;
}

function CounterRow({
  icon,
  iconCls,
  label,
  count,
  isFuture = false,
  to,
  testId,
}: CounterRowProps) {
  const navigate = useNavigate();
  const clickable = !isFuture && Boolean(to);

  const body = (
    <>
      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${iconCls}`}>
        <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5" />
      </div>
      <span className={`flex-1 text-sm ${isFuture ? 'text-muted-foreground' : 'text-foreground'}`}>
        {label}
      </span>
      {isFuture ? (
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
          soon
        </span>
      ) : (
        // A genuine zero renders no badge at all. The row stays visible so the
        // operator can still navigate, but an empty queue is not dressed up as
        // a data point.
        count > 0 && (
          <span className="min-w-[1.5rem] rounded-full bg-destructive/10 px-2 py-0.5 text-center text-xs font-semibold text-destructive tabular-nums">
            {count}
          </span>
        )
      )}
    </>
  );

  if (!clickable) {
    return (
      <div className="flex items-center gap-3 py-2" data-testid={testId}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={(e) => {
        // The whole card also navigates; without this the row's own
        // destination would be overridden by the card's.
        e.stopPropagation();
        navigate(to as string);
      }}
      className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted/60 focus:outline-none focus-visible:bg-muted/60"
    >
      {body}
    </button>
  );
}

interface RequestCenterCardProps {
  counts: RequestCenterCounts;
}

export function RequestCenterCard({ counts }: RequestCenterCardProps) {
  const navigate = useNavigate();
  // Authorization requests are now real data, so they count toward the
  // "actions required" summary alongside transfers, takeovers and returns.
  const total =
    counts.transferRequests +
    counts.takeoverRequests +
    counts.rejectedItems +
    counts.authorizationRequests;

  return (
    <div
      onClick={() => navigate(ROUTES.REQUEST_CENTER)}
      className="cursor-pointer rounded-xl border border-border bg-card p-5 hover:border-primary/50 hover:shadow-sm transition-all"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-500/10 text-violet-700">
            <FontAwesomeIcon icon={faInbox} className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Request Center</p>
            {total > 0 ? (
              <p className="text-xs text-destructive font-medium">
                {total} action{total !== 1 ? 's' : ''} required
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">All clear</p>
            )}
          </div>
        </div>
        <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-muted-foreground" />
      </div>

      <div className="divide-y divide-border">
        <CounterRow
          icon={faClipboardCheck}
          iconCls="bg-blue-500/10 text-blue-700"
          label="Inspection Requests"
          count={counts.inspectionRequests}
          to={`${ROUTES.MY_INSPECTIONS}?tab=assigned`}
          testId="rc-inspection-requests"
        />
        <CounterRow
          icon={faArrowRightArrowLeft}
          iconCls="bg-amber-500/10 text-amber-700"
          label="Transfer Requests"
          count={counts.transferRequests}
          to={ROUTES.REQUEST_CENTER}
          testId="rc-transfer-requests"
        />
        <CounterRow
          icon={faCodeBranch}
          iconCls="bg-orange-500/10 text-orange-700"
          label="Takeover Requests"
          count={counts.takeoverRequests}
          to={ROUTES.REQUEST_CENTER}
          testId="rc-takeover-requests"
        />
        <CounterRow
          icon={faShieldHalved}
          iconCls="bg-slate-500/10 text-slate-600"
          label="Authorization Requests"
          count={counts.authorizationRequests}
          to={ROUTES.AUTHORIZATION_CENTER}
          testId="rc-authorization-requests"
        />
        <CounterRow
          icon={faEye}
          iconCls="bg-slate-500/10 text-slate-600"
          label="Pending Reviews"
          count={counts.pendingReviews}
          isFuture
        />
        <CounterRow
          icon={faUserCheck}
          iconCls="bg-slate-500/10 text-slate-600"
          label="Customer Approvals"
          count={counts.customerApprovals}
          isFuture
        />
        <CounterRow
          icon={faCircleXmark}
          iconCls="bg-red-500/10 text-red-700"
          label="Rejected Items"
          count={counts.rejectedItems}
        />
      </div>
    </div>
  );
}

export function RequestCenterCardSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-lg skeleton-shimmer" />
        <div className="space-y-1.5">
          <div className="h-4 w-28 rounded skeleton-shimmer" />
          <div className="h-3 w-20 rounded skeleton-shimmer" />
        </div>
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 py-1">
          <div className="h-7 w-7 rounded-lg skeleton-shimmer" />
          <div className="h-3.5 flex-1 rounded skeleton-shimmer" />
          <div className="h-5 w-6 rounded-full skeleton-shimmer" />
        </div>
      ))}
    </div>
  );
}
